import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Admin from "@/pages/Admin";
import AppLayout from "@/components/AppLayout";
import ContentCommandCenter from "@/pages/ContentCommandCenter";
import BrandCenter from "@/pages/BrandCenter";
import IdeaVault from "@/pages/IdeaVault";
import SOPLibrary from "@/pages/SOPLibrary";
import Planning from "@/pages/Planning";
import AIBusinessGuide from "@/pages/AIBusinessGuide";
import Projects from "@/pages/Projects";
import GrantFinder from "@/pages/GrantFinder";
import CRM from "@/pages/CRM";
import Email from "@/pages/Email";
import KnowledgeLibrary from "@/pages/KnowledgeLibrary";
import BusinessCenter from "@/pages/BusinessCenter";
import BusinessSetup from "@/pages/BusinessSetup";
import Settings from "@/pages/Settings";
import { Loader2 } from "lucide-react";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" data-testid="app-loading-spinner" />
    </div>
  );
}

function ProtectedRoute() {
  const { user, loading, hasAccess } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAccess) return <Navigate to="/billing" replace />;
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

// Public marketing/sales page at "/". Authenticated users are sent into the app.
function LandingGate() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

// Auth required, but NO subscription required (billing + payment result screens).
function AuthOnlyRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function AppRouter() {
  const location = useLocation();
  // Process the OAuth session_id FIRST, before any protected-route checks.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingGate />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route element={<AuthOnlyRoute />}>
        <Route path="/billing" element={<Billing />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
      <Route element={<ProtectedRoute />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/content" element={<ContentCommandCenter />} />
        <Route path="/brand" element={<BrandCenter />} />
        <Route path="/ideas" element={<IdeaVault />} />
        <Route path="/sops" element={<SOPLibrary />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/guide" element={<AIBusinessGuide />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/grants" element={<GrantFinder />} />
        <Route path="/crm" element={<CRM />} />
        <Route path="/email" element={<Email />} />
        <Route path="/knowledge" element={<KnowledgeLibrary />} />
        <Route path="/ai" element={<Navigate to="/guide" replace />} />
        <Route path="/business" element={<BusinessCenter />} />
        <Route path="/setup" element={<BusinessSetup />} />
        <Route path="/planning" element={<Planning />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
